import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Heart, Send, Plus, Loader2, BarChart3, Award, HandHeart, Lightbulb, GraduationCap, Rocket, Users, Check, XCircle, Clock as ClockIcon, Eye, Wand2, Sparkles, MessageSquareText } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PIE_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#14b8a6'];

const BADGE_ICONS = {
  helpful: HandHeart, teamwork: Users, innovator: Lightbulb, mentor: GraduationCap, goabove: Rocket,
};

const EngagePage = () => {
  const { user, isHR } = useAuth();
  const [wall, setWall] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [badgeConfig, setBadgeConfig] = useState(null);
  const [myBadges, setMyBadges] = useState(null);
  const [loading, setLoading] = useState(true);
  const [praiseOpen, setPraiseOpen] = useState(false);
  const [surveyOpen, setSurveyOpen] = useState(false);
  const [praiseForm, setPraiseForm] = useState({ to_employee_id: '', badge_type: 'helpful', message: '' });
  const [surveyForm, setSurveyForm] = useState({ question: '', options: ['', ''], duration_hours: 24, survey_type: 'poll', anonymous_strict: false });
  const [submitting, setSubmitting] = useState(false);
  const [resultsOpen, setResultsOpen] = useState(null); // survey object
  const [suggestions, setSuggestions] = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestSource, setSuggestSource] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [wallRes, surveyRes, empRes, configRes, badgesRes] = await Promise.allSettled([
        axios.get(`${API_URL}/api/badges/wall`, { withCredentials: true }),
        axios.get(`${API_URL}/api/surveys`, { withCredentials: true }),
        axios.get(`${API_URL}/api/employees`, { withCredentials: true }),
        axios.get(`${API_URL}/api/badges/config`, { withCredentials: true }),
        axios.get(`${API_URL}/api/badges/my-badges`, { withCredentials: true }),
      ]);
      if (wallRes.status === 'fulfilled') setWall(wallRes.value.data);
      if (surveyRes.status === 'fulfilled') setSurveys(surveyRes.value.data);
      if (empRes.status === 'fulfilled') setEmployees(empRes.value.data);
      if (configRes.status === 'fulfilled') setBadgeConfig(configRes.value.data);
      if (badgesRes.status === 'fulfilled') setMyBadges(badgesRes.value.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handlePraise = async () => {
    if (!praiseForm.to_employee_id) { toast.error('Select a colleague'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/badges/praise`, praiseForm, { withCredentials: true });
      toast.success('Praise sent!');
      setPraiseOpen(false);
      setPraiseForm({ to_employee_id: '', badge_type: 'helpful', message: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const handleVote = async (surveyId, optionId) => {
    try {
      await axios.post(`${API_URL}/api/surveys/${surveyId}/vote`, { option_id: optionId }, { withCredentials: true });
      toast.success('Vote recorded!');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Already voted'); }
  };

  const handleSubmitFeedback = async (surveyId, text) => {
    if (!text.trim()) { toast.error('Please write your feedback'); return; }
    try {
      await axios.post(`${API_URL}/api/surveys/${surveyId}/vote`, { response_text: text.trim() }, { withCredentials: true });
      toast.success('Feedback submitted anonymously');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit'); }
  };

  const handleCreateSurvey = async () => {
    if (!surveyForm.question.trim()) { toast.error('Question is required'); return; }
    const isFeedback = surveyForm.survey_type === 'feedback';
    const opts = isFeedback ? [] : surveyForm.options.filter(o => o.trim());
    if (!isFeedback && opts.length < 2) { toast.error('Need at least 2 options for a poll'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/surveys`, {
        question: surveyForm.question,
        options: opts,
        duration_hours: parseInt(surveyForm.duration_hours) || 24,
        survey_type: surveyForm.survey_type,
        anonymous_strict: !!surveyForm.anonymous_strict,
      }, { withCredentials: true });
      toast.success(`${isFeedback ? 'Feedback form' : 'Poll'} created!`);
      setSurveyOpen(false);
      setSurveyForm({ question: '', options: ['', ''], duration_hours: 24, survey_type: 'poll', anonymous_strict: false });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const handleCloseSurvey = async (surveyId) => {
    if (!window.confirm('Close this survey now? Voting will stop and results will be finalized.')) return;
    try {
      await axios.post(`${API_URL}/api/surveys/${surveyId}/close`, {}, { withCredentials: true });
      toast.success('Survey closed');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleViewResults = async (survey) => {
    try {
      const res = await axios.get(`${API_URL}/api/surveys/${survey.id}/results`, { withCredentials: true });
      setResultsOpen({ ...survey, ...res.data, voters_by_option: res.data.voters_by_option || {} });
    } catch (err) { toast.error('Failed to load results'); }
  };

  const formatExpiry = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    const now = new Date();
    const ms = d - now;
    if (ms < 0) return 'Expired';
    const minutes = Math.floor(ms / 60000);
    const hours = Math.floor(minutes / 60);
    if (minutes < 60) return minutes < 5 ? 'Closing soon' : `${minutes}m left`;
    if (hours < 24) return `${hours}h left`;
    return `${Math.floor(hours / 24)}d left`;
  };

  // PIE_COLORS is now declared at module scope (above)

  const handleFetchSuggestions = async () => {
    setSuggestLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/surveys/ai-suggestions`, { withCredentials: true });
      setSuggestions(res.data?.suggestions || []);
      setSuggestSource(res.data?.source || '');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSuggestLoading(false); }
  };

  const applySuggestion = (sug) => {
    setSurveyForm({ question: sug.question, options: sug.options.length >= 2 ? sug.options : [...sug.options, ''], duration_hours: 24 });
    toast.success('Filled — review & publish!');
  };

  // Auto-fetch suggestions when admin opens the New Survey dialog
  useEffect(() => {
    if (surveyOpen && isHR && suggestions.length === 0) {
      handleFetchSuggestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyOpen]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="engage-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Heart className="w-7 h-7 text-rose-500" /> Engage</h1>
          <p className="text-muted-foreground">{badgeConfig?.wall_instruction || 'Celebrate colleagues and share your voice'}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button className="gap-2" onClick={() => setPraiseOpen(true)} data-testid="give-praise-btn"><Award className="w-4 h-4" /> Give Praise</Button>
          {isHR && <Button variant="outline" className="gap-2" onClick={() => { setSurveyForm(f => ({ ...f, survey_type: 'poll' })); setSurveyOpen(true); }} data-testid="create-survey-btn"><Plus className="w-4 h-4" /> New Poll</Button>}
          {isHR && <Button variant="outline" className="gap-2" onClick={() => { setSurveyForm(f => ({ ...f, survey_type: 'feedback', anonymous_strict: true, options: [] })); setSurveyOpen(true); }} data-testid="create-feedback-btn"><MessageSquareText className="w-4 h-4" /> New Feedback Form</Button>}
        </div>
      </div>

      <Tabs defaultValue="wall">
        <TabsList>
          <TabsTrigger value="wall" className="gap-2"><Award className="w-4 h-4" /> Praise Wall</TabsTrigger>
          <TabsTrigger value="surveys" className="gap-2"><BarChart3 className="w-4 h-4" /> Pulse Surveys</TabsTrigger>
          <TabsTrigger value="mybadges" className="gap-2"><Heart className="w-4 h-4" /> My Badges</TabsTrigger>
        </TabsList>

        <TabsContent value="wall" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {wall.length > 0 ? wall.map(p => {
              const Icon = BADGE_ICONS[p.badge_type] || Award;
              return (
                <Card key={p.id} className="hover:shadow-md transition-shadow" data-testid={`praise-${p.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5 text-amber-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm"><span className="font-semibold">{p.from_name}</span> praised <span className="font-semibold text-primary">{p.to_name}</span></p>
                        <Badge variant="secondary" className="mt-1 text-xs">{badgeConfig?.badge_types?.find(b => b.key === p.badge_type)?.label || p.badge_type}</Badge>
                        {p.message && <p className="text-sm text-muted-foreground mt-2 italic">"{p.message}"</p>}
                        <p className="text-[10px] text-muted-foreground mt-1">{new Date(p.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            }) : <p className="text-center text-muted-foreground py-8 col-span-2">No praise yet. Be the first to recognize a colleague!</p>}
          </div>
        </TabsContent>

        <TabsContent value="surveys" className="mt-4">
          <div className="space-y-4">
            {surveys.length > 0 ? surveys.map(s => {
              const isClosed = s.status === 'closed';
              const isFeedback = s.survey_type === 'feedback';
              const expiryLabel = formatExpiry(s.expires_at);
              return (
              <Card key={s.id} data-testid={`survey-${s.id}`} className={isClosed ? 'opacity-90 border-dashed' : ''}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-base flex items-center gap-2">{s.question}
                        {isFeedback && <Badge variant="outline" className="text-[10px] bg-blue-50 dark:bg-blue-950/20 text-blue-700 dark:text-blue-400">Feedback</Badge>}
                        {s.anonymous_strict && <Badge variant="outline" className="text-[10px] bg-purple-50 dark:bg-purple-950/20 text-purple-700 dark:text-purple-400" title="Strict anonymity — even admins can't see who responded">🔒 Anonymous</Badge>}
                      </CardTitle>
                      <CardDescription className="flex items-center gap-2 flex-wrap">
                        <span>{isFeedback ? `${s.feedback_count || 0} response(s)` : `${s.total_votes} vote(s)`}</span>
                        {isClosed ? (
                          <Badge variant="outline" className="text-[10px] gap-1 bg-slate-100 dark:bg-slate-800"><XCircle className="w-3 h-3" /> Closed</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] gap-1 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400"><ClockIcon className="w-3 h-3" /> {expiryLabel || 'Active'}</Badge>
                        )}
                      </CardDescription>
                    </div>
                    {isHR && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={() => handleViewResults(s)} data-testid={`view-results-${s.id}`}><Eye className="w-3 h-3" /> Results</Button>
                        {!isClosed && <Button size="sm" variant="outline" className="h-7 gap-1 text-xs text-amber-700" onClick={() => handleCloseSurvey(s.id)} data-testid={`close-survey-${s.id}`}><XCircle className="w-3 h-3" /> Close</Button>}
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {isFeedback ? (
                    isClosed ? (
                      <p className="text-center text-muted-foreground text-sm py-2">Closed — admins can review responses via "Results".</p>
                    ) : s.my_vote ? (
                      <div className="p-3 rounded-md bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/40 text-sm text-emerald-800 dark:text-emerald-300 flex items-center gap-2">
                        <Check className="w-4 h-4" /> Your feedback has been recorded {s.anonymous_strict ? 'anonymously.' : '.'}
                      </div>
                    ) : (
                      <FeedbackComposer surveyId={s.id} onSubmit={handleSubmitFeedback} strict={s.anonymous_strict} />
                    )
                  ) : isClosed ? (
                    /* Closed poll: show pie chart of results */
                    s.total_votes > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                        <ResponsiveContainer width="100%" height={200}>
                          <PieChart>
                            <Pie data={s.options.map(o => ({ name: o.text, value: o.votes }))} cx="50%" cy="50%" outerRadius={70} fill="#8884d8" dataKey="value" label={(e) => `${Math.round(e.percent * 100)}%`}>
                              {s.options.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                            </Pie>
                            <Tooltip />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="space-y-2">
                          {s.options.map((opt, i) => {
                            const pct = s.total_votes > 0 ? Math.round((opt.votes / s.total_votes) * 100) : 0;
                            return (
                              <div key={opt.id} className="text-sm">
                                <div className="flex items-center justify-between">
                                  <span className="flex items-center gap-2"><span className="w-3 h-3 rounded" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />{opt.text}</span>
                                  <span className="font-mono text-xs"><b>{opt.votes}</b> ({pct}%)</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : <p className="text-center text-muted-foreground text-sm py-4">No votes were cast on this survey.</p>
                  ) : (
                    /* Active: voting UI */
                    <div className="space-y-2">
                      {s.options.map(opt => {
                        const pct = s.total_votes > 0 ? Math.round((opt.votes / s.total_votes) * 100) : 0;
                        const isMyVote = s.my_vote === opt.id;
                        return (
                          <div key={opt.id} className={`relative p-3 rounded-lg border cursor-pointer transition-all ${isMyVote ? 'border-primary bg-primary/5' : s.my_vote ? 'bg-muted/30' : 'hover:border-primary/50 hover:bg-muted/30'}`}
                            onClick={() => !s.my_vote && handleVote(s.id, opt.id)}>
                            <div className="flex items-center justify-between relative z-10">
                              <span className="text-sm font-medium">{opt.text}</span>
                              <span className="text-xs font-mono text-muted-foreground">{s.my_vote ? `${pct}%` : ''}</span>
                            </div>
                            {s.my_vote && <Progress value={pct} className="h-1.5 mt-1.5" />}
                            {isMyVote && <Check className="absolute right-3 top-3 w-4 h-4 text-primary" />}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
              );
            }) : <p className="text-center text-muted-foreground py-8">No active surveys. {isHR ? 'Create one!' : 'Check back later.'}</p>}
          </div>
        </TabsContent>

        <TabsContent value="mybadges" className="mt-4">
          {myBadges ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                {badgeConfig?.badge_types?.map(bt => {
                  const Icon = BADGE_ICONS[bt.key] || Award;
                  const count = myBadges.summary[bt.key] || 0;
                  return (
                    <Card key={bt.key} className={count > 0 ? 'border-amber-200 dark:border-amber-800' : 'opacity-50'}>
                      <CardContent className="p-4 text-center">
                        <Icon className={`w-8 h-8 mx-auto ${count > 0 ? 'text-amber-500' : 'text-muted-foreground'}`} />
                        <p className="text-2xl font-bold mt-1">{count}</p>
                        <p className="text-xs text-muted-foreground">{bt.label}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              <p className="text-center text-sm text-muted-foreground">Total badges received: {myBadges.total}</p>
            </div>
          ) : <p className="text-center text-muted-foreground py-8">No badges yet</p>}
        </TabsContent>
      </Tabs>

      {/* Give Praise Dialog */}
      <Dialog open={praiseOpen} onOpenChange={setPraiseOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Award className="w-5 h-5" /> Give Praise</DialogTitle><DialogDescription>Recognize a colleague's contribution</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Colleague</Label>
              <Select value={praiseForm.to_employee_id} onValueChange={v => setPraiseForm({ ...praiseForm, to_employee_id: v })}>
                <SelectTrigger data-testid="praise-to"><SelectValue placeholder="Select colleague" /></SelectTrigger>
                <SelectContent>{employees.filter(e => e.status === 'active').map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Badge Type</Label>
              <div className="grid grid-cols-3 gap-2">
                {badgeConfig?.badge_types?.map(bt => {
                  const Icon = BADGE_ICONS[bt.key] || Award;
                  return (
                    <button key={bt.key} type="button" className={`p-2 rounded-lg border text-center transition-colors ${praiseForm.badge_type === bt.key ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/20' : 'hover:bg-muted'}`}
                      onClick={() => setPraiseForm({ ...praiseForm, badge_type: bt.key })}>
                      <Icon className="w-5 h-5 mx-auto text-amber-500" />
                      <p className="text-[10px] mt-1">{bt.label}</p>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-2"><Label>Message (optional)</Label><Textarea value={praiseForm.message} onChange={e => setPraiseForm({ ...praiseForm, message: e.target.value })} placeholder="What did they do great?" rows={2} data-testid="praise-message" /></div>
            <Button className="w-full gap-2" onClick={handlePraise} disabled={submitting} data-testid="send-praise-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send Praise
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Survey/Feedback Dialog */}
      <Dialog open={surveyOpen} onOpenChange={setSurveyOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><BarChart3 className="w-5 h-5" /> Create {surveyForm.survey_type === 'feedback' ? 'Feedback Form' : 'Poll'}</DialogTitle><DialogDescription>Quick pulse check — anonymous for employees</DialogDescription></DialogHeader>
          <div className="space-y-4">
            {/* Type Toggle */}
            <div className="space-y-2">
              <Label>Survey Type</Label>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setSurveyForm({ ...surveyForm, survey_type: 'poll' })} className={`p-3 rounded-lg border text-left transition-colors ${surveyForm.survey_type === 'poll' ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}`} data-testid="survey-type-poll">
                  <p className="text-sm font-semibold">Poll</p>
                  <p className="text-xs text-muted-foreground">Multiple-choice, quick tally</p>
                </button>
                <button type="button" onClick={() => setSurveyForm({ ...surveyForm, survey_type: 'feedback' })} className={`p-3 rounded-lg border text-left transition-colors ${surveyForm.survey_type === 'feedback' ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}`} data-testid="survey-type-feedback">
                  <p className="text-sm font-semibold">Feedback Form</p>
                  <p className="text-xs text-muted-foreground">Open-text, qualitative input</p>
                </button>
              </div>
            </div>

            <div className="space-y-2"><Label>Question / Prompt</Label><Input value={surveyForm.question} onChange={e => setSurveyForm({ ...surveyForm, question: e.target.value })} placeholder={surveyForm.survey_type === 'feedback' ? 'e.g., "What is one thing we could do better this month?"' : 'e.g., "How is the vibe today?"'} data-testid="survey-question" /></div>

            {/* AI Suggestions */}
            {surveyForm.survey_type === 'poll' && (
            <div className="rounded-lg border border-purple-200 dark:border-purple-900/40 bg-purple-50/40 dark:bg-purple-900/10 p-3 space-y-2" data-testid="ai-suggestions-block">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wider text-purple-700 dark:text-purple-400 flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> AI suggestions {suggestSource === 'ai' && <Badge variant="outline" className="text-[9px] h-4 px-1">contextual</Badge>}</p>
                <Button type="button" variant="ghost" size="sm" className="h-6 gap-1 text-xs text-purple-700" onClick={handleFetchSuggestions} disabled={suggestLoading} data-testid="refresh-suggestions-btn">
                  {suggestLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />} {suggestions.length === 0 ? 'Suggest' : 'Refresh'}
                </Button>
              </div>
              {suggestLoading && suggestions.length === 0 ? (
                <p className="text-xs text-muted-foreground">Generating ideas based on this week's activity…</p>
              ) : suggestions.length > 0 ? (
                <div className="space-y-1.5">
                  {suggestions.map((sug, i) => (
                    <button key={i} type="button" onClick={() => applySuggestion(sug)} className="w-full text-left p-2 rounded border bg-white dark:bg-slate-900 hover:border-purple-400 hover:shadow-sm transition-all" data-testid={`suggestion-${i}`}>
                      <p className="text-sm font-medium">{sug.question}</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">{sug.options.join(' · ')}</p>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">Click "Suggest" to get 3 contextual ideas powered by AI.</p>
              )}
            </div>
            )}

            {surveyForm.survey_type === 'poll' && (
            <div className="space-y-2">
              <Label>Options</Label>
              {surveyForm.options.map((opt, i) => (
                <Input key={i} value={opt} onChange={e => { const opts = [...surveyForm.options]; opts[i] = e.target.value; setSurveyForm({ ...surveyForm, options: opts }); }} placeholder={`Option ${i + 1}`} data-testid={`survey-opt-${i}`} />
              ))}
              <Button variant="ghost" size="sm" className="gap-1" onClick={() => setSurveyForm({ ...surveyForm, options: [...surveyForm.options, ''] })}><Plus className="w-3 h-3" /> Add option</Button>
            </div>
            )}

            <div className="space-y-2">
              <Label>Duration</Label>
              <Select value={String(surveyForm.duration_hours)} onValueChange={v => setSurveyForm({ ...surveyForm, duration_hours: parseInt(v) })}>
                <SelectTrigger data-testid="survey-duration"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="2">2 hours</SelectItem>
                  <SelectItem value="8">8 hours</SelectItem>
                  <SelectItem value="24">1 day (default)</SelectItem>
                  <SelectItem value="72">3 days</SelectItem>
                  <SelectItem value="168">1 week</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Survey auto-closes after this duration. You can also close it manually.</p>
            </div>

            <div className="rounded-lg border bg-muted/30 p-3 flex items-start gap-3">
              <input id="anon-strict" type="checkbox" checked={!!surveyForm.anonymous_strict} onChange={e => setSurveyForm({ ...surveyForm, anonymous_strict: e.target.checked })} className="mt-1" data-testid="anonymous-strict-toggle" />
              <label htmlFor="anon-strict" className="text-sm cursor-pointer">
                <span className="font-semibold">Strict anonymity</span>
                <p className="text-xs text-muted-foreground mt-0.5">Even admins/HR will not see who responded. Useful for sensitive topics. Defaults to OFF for poll, recommended ON for feedback forms.</p>
              </label>
            </div>

            <Button className="w-full gap-2" onClick={handleCreateSurvey} disabled={submitting} data-testid="create-survey-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />} Create {surveyForm.survey_type === 'feedback' ? 'Feedback Form' : 'Poll'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Survey Results Dialog (Admin) */}
      <Dialog open={!!resultsOpen} onOpenChange={(o) => !o && setResultsOpen(null)}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="survey-results-dialog">
          <DialogHeader>
            <DialogTitle>{resultsOpen?.question}</DialogTitle>
            <DialogDescription>
              {resultsOpen?.survey_type === 'feedback'
                ? `${resultsOpen?.feedback_count || 0} response(s) · Status: ${resultsOpen?.status}`
                : `${resultsOpen?.total_votes || 0} vote(s) · Status: ${resultsOpen?.status}${resultsOpen?.closed_by ? ` (closed by ${resultsOpen.closed_by})` : ''}`}
            </DialogDescription>
          </DialogHeader>
          {resultsOpen && (
            <FeedbackOrPollResults survey={resultsOpen} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EngagePage;

// Inline helper: open-text feedback composer for feedback-type surveys
const FeedbackComposer = ({ surveyId, onSubmit, strict }) => {
  const [text, setText] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  return (
    <div className="space-y-2" data-testid={`feedback-composer-${surveyId}`}>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Share your honest thoughts..."
        rows={3}
        maxLength={2000}
        className="w-full text-sm p-2 rounded border bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
        data-testid={`feedback-textarea-${surveyId}`}
      />
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">{strict ? 'Strict anonymity: not even admins see your name.' : 'Anonymous to colleagues; admins may view.'}</p>
        <button
          type="button"
          disabled={busy || !text.trim()}
          onClick={async () => { setBusy(true); try { await onSubmit(surveyId, text); setText(''); } finally { setBusy(false); } }}
          className="px-3 py-1.5 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          data-testid={`feedback-submit-${surveyId}`}
        >
          {busy ? 'Sending…' : 'Submit'}
        </button>
      </div>
    </div>
  );
};

// Results renderer for both poll and feedback surveys
const FeedbackOrPollResults = ({ survey }) => {
  const isFeedback = survey.survey_type === 'feedback' || survey.type === 'feedback';
  if (isFeedback) {
    const responses = survey.responses || [];
    return (
      <div className="space-y-3" data-testid="feedback-results">
        {survey.anonymous_strict && (
          <div className="rounded-md bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-900/40 p-2 text-xs text-purple-700 dark:text-purple-300">
            🔒 Strict-anonymous mode — identities are not stored.
          </div>
        )}
        {responses.length > 0 ? responses.map((r, i) => (
          <div key={r.id || i} className="rounded-md border p-3 bg-muted/20" data-testid={`feedback-response-${i}`}>
            <p className="text-sm whitespace-pre-wrap">{r.response_text}</p>
            <p className="text-[10px] text-muted-foreground mt-2">
              {r.employee_name ? `— ${r.employee_name}` : <span className="italic">Anonymous</span>}
              {' · '}{new Date(r.created_at).toLocaleString('en-IN')}
            </p>
          </div>
        )) : <p className="text-center text-muted-foreground text-sm py-4">No responses yet.</p>}
      </div>
    );
  }
  // Poll results
  return (
    <div className="space-y-4" data-testid="poll-results">
      {survey.anonymous_strict_notice && (
        <div className="rounded-md bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-900/40 p-2 text-xs text-purple-700 dark:text-purple-300">
          🔒 {survey.anonymous_strict_notice}
        </div>
      )}
      {(survey.total_votes || 0) > 0 ? (
        <ResponsiveContainer width="100%" height={220}>
          <PieChart>
            <Pie data={survey.options.map(o => ({ name: o.text, value: o.votes }))} cx="50%" cy="50%" outerRadius={80} fill="#8884d8" dataKey="value" label={(e) => `${Math.round(e.percent * 100)}%`}>
              {survey.options.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
            </Pie>
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 12 }} />
          </PieChart>
        </ResponsiveContainer>
      ) : <p className="text-center text-muted-foreground text-sm">No votes yet.</p>}
      <div className="space-y-3">
        {survey.options.map((opt, i) => {
          const voters = survey.voters_by_option?.[opt.id] || [];
          return (
            <div key={opt.id} className="border rounded p-3" data-testid={`results-option-${i}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-semibold flex items-center gap-2"><span className="w-3 h-3 rounded" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />{opt.text}</span>
                <Badge variant="outline" className="text-xs">{opt.votes} vote(s)</Badge>
              </div>
              {survey.anonymous_strict ? (
                <p className="text-xs text-muted-foreground italic">Anonymous votes — voter list hidden.</p>
              ) : voters.length > 0 ? (
                <p className="text-xs text-muted-foreground">Voted by: <span className="text-foreground">{voters.join(', ')}</span></p>
              ) : <p className="text-xs text-muted-foreground italic">No voters</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
};
